import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PartnerPage = () => {
  const { t } = useTranslation();
  const benefits = t('landing.partner.benefits', { returnObjects: true });
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    partner_type: 'reseller',
    region: '',
    experience: '',
    notes: '',
    accept_terms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.accept_terms) { setErr(t('landing.partner.accept')); return; }
    try {
      setSubmitting(true);
      await axios.post(`${API}/api/partner-applications`, form);
      setSent(true);
    } catch (er) {
      setErr(er?.response?.data?.detail || t('landing.partner.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
          <h1 className="text-2xl font-semibold mb-3">{t('landing.partner.successTitle')}</h1>
          <p className="text-gray-600 mb-6">{t('landing.partner.successBody')}</p>
          <Link to="/" className="text-blue-600 hover:underline">← {t('landing.partner.backHome')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <header className="text-center mb-10">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-3">{t('landing.partner.title')}</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('landing.partner.subtitle')}</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Benefits sidebar */}
          <aside className="lg:col-span-1 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('landing.partner.benefitsTitle')}</h2>
            {Array.isArray(benefits) && benefits.map((b) => (
              <div key={b.h} className="bg-white rounded-lg shadow-sm p-4">
                <div className="font-semibold mb-1">{b.h}</div>
                <p className="text-sm text-gray-600">{b.p}</p>
              </div>
            ))}
          </aside>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-xl shadow-md p-8 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium">{t('landing.partner.companyName')} *</span>
                <input type="text" required className="input w-full mt-1"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm font-medium">{t('landing.partner.contactName')} *</span>
                <input type="text" required className="input w-full mt-1"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm font-medium">{t('landing.partner.email')} *</span>
                <input type="email" required className="input w-full mt-1"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </label>
              <label className="block">
                <span className="text-sm font-medium">{t('landing.partner.phone')} *</span>
                <input type="tel" required className="input w-full mt-1"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+90 5XX XXX XX XX" />
              </label>
              <label className="block">
                <span className="text-sm font-medium">{t('landing.partner.partnerType')} *</span>
                <select className="input w-full mt-1"
                  value={form.partner_type}
                  onChange={(e) => setForm({ ...form, partner_type: e.target.value })}>
                  <option value="reseller">{t('landing.partner.typeReseller')}</option>
                  <option value="integrator">{t('landing.partner.typeIntegrator')}</option>
                  <option value="consultant">{t('landing.partner.typeConsultant')}</option>
                </select>
              </label>
              <label className="block">
                <span className="text-sm font-medium">{t('landing.partner.region')}</span>
                <input type="text" className="input w-full mt-1"
                  value={form.region}
                  onChange={(e) => setForm({ ...form, region: e.target.value })}
                  placeholder={t('landing.partner.regionPlaceholder')} />
              </label>
            </div>
            <label className="block">
              <span className="text-sm font-medium">{t('landing.partner.experience')}</span>
              <textarea rows="3" className="input w-full mt-1"
                value={form.experience}
                onChange={(e) => setForm({ ...form, experience: e.target.value })}
                placeholder={t('landing.partner.experiencePlaceholder')} />
            </label>
            <label className="block">
              <span className="text-sm font-medium">{t('landing.partner.notes')}</span>
              <textarea rows="2" className="input w-full mt-1"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={form.accept_terms}
                onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })}
                className="mt-1" />
              <span>
                {t('landing.partner.accept')}{' '}
                <Link to="/privacy" target="_blank" rel="noreferrer" className="text-blue-600 underline">
                  KVKK / GDPR
                </Link>
              </span>
            </label>
            {err && <div className="text-red-600 text-sm">{err}</div>}
            <button type="submit" disabled={submitting} className="btn-primary w-full py-3 text-lg">
              {submitting ? t('landing.partner.submitting') : t('landing.partner.submit')}
            </button>
            <p className="text-center text-xs text-gray-500">
              <Link to="/" className="hover:underline">← {t('landing.partner.backHome')}</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PartnerPage;
